import { DefaultButton, Icon, Pivot, PivotItem, Text } from '@fluentui/react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { genkey, PARTIDOS_EXTRA_ZONA_NAME } from '../../common/constants';
import { Club, todosContraTodos, Torneo } from '../../system/SystemClient';
import ClubImageCircle from '../Common/ClubImageCircle';
import { ZonaFixture } from '../Common/ZonaFixture';
import TorneoTabFixture from './TorneoTabFixture';
import TorneoTabGoleadores from './TorneoTabGoleadores';
import TorneoTabTablaPosiciones from './TorneoTabTablaPosiciones';
import TorneoTabTablaGeneralPosicionesFairplay from './TorneoTabTablaPosicionesFairplay';

interface ITorneoContainer {
  torneo: Torneo;
  hideBack?: boolean;
  hideRefresh?: boolean;
  setRefresh?: (v: boolean) => void;
  refresh?: boolean;
  lastUpdate?: Date;
  highlightClubId?: string;
  club: Club | undefined;
}

export const TorneoContainer = ({
  torneo,
  hideBack,
  hideRefresh,
  setRefresh,
  refresh,
  lastUpdate,
  highlightClubId,
  club,
}: ITorneoContainer) => {
  const navigate = useNavigate();

  const selectedClubId = club?.clubId ?? highlightClubId;
  const goHome = () => {
    const target = selectedClubId ? `/?clubId=${encodeURIComponent(String(selectedClubId))}` : '/';
    navigate(target);
  };

  return (
    <>
      <div className="ms-Grid mt-2" dir="ltr" style={{ marginBottom: 10 }}></div>
      <div className="ms-Grid-col ms-sm12">
        <div className="ms-Grid-row " style={{ display: 'flex' }}>
          <div className="ms-Grid-col ms-sm12 mb-5">
            <div className="ms-Grid bg-white-h100 ms-depth-4" dir="ltr">
              <>
                <div className="ms-Grid-row padding-1">
                  <div className="ms-Grid-col ms-sm12 text-center"></div>
                  <div className="ms-Grid-col ms-md8 ms-sm8">
                    <Text className="nombre-header-torneo d-block">{torneo.detalle.nombreTorneo}</Text>
                    <Text variant="small" className="d-block">
                      {torneo.detalle.temporada.temporadaNombre} - {torneo.detalle.categoria.categoriaNombre}{' '}
                      -{' '}
                      {torneo.detalle.division.divisionNombre
                        ? torneo.detalle.division.divisionNombre + ' - '
                        : ''}
                      {torneo.detalle.rama === 'M' ? 'Masculino ' : 'Femenino '}
                    </Text>
                    {Number(club?.clubId) > 0 ? (
                      <div className="mt-1">
                        <ClubImageCircle
                          escudo={club?.clubEscudo}
                          width={25}
                          height={25}
                          float="left"
                        ></ClubImageCircle>
                        <Text className="ml-1">{club?.club}</Text>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                  <div className="ms-Grid-col ms-md4 ms-sm4">
                    <div className="ms-Grid-row mt-1">
                      {!hideBack && (
                        <div className="ms-Grid-col ms-sm12">
                          <DefaultButton className="float-right" onClick={goHome} type="button">
                            <Icon iconName="Back" className="mr-1" />
                          </DefaultButton>
                        </div>
                      )}
                      {!hideRefresh && setRefresh && lastUpdate && (
                        <div className="ms-Grid-col ms-sm12 mt-1">
                          <Text
                            variant="small"
                            className="link-styles clickable float-right"
                            onClick={() => {
                              setRefresh(!refresh);
                            }}
                          >
                            {DateTime.fromJSDate(lastUpdate).toFormat('dd/LL HH:mm')}
                            <Icon className="ml-1 " iconName="Refresh"></Icon>
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className="ms-Grid-col ms-sm12"
                  style={{ borderTop: '1px solid rgb(232, 232, 232)' }}
                ></div>
                <div className="ms-Grid-col ms-sm12" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <div className="ms-Grid-row padding-row-custom">
                    <Pivot id="fixture_pos_gol" aria-label="" className="text-center customFocusZone">
                      <PivotItem
                        headerText="Fixture"
                        itemKey={'1'}
                        onRenderItemLink={(itemProps) => {
                          return (
                            <div>
                              <span>{itemProps?.headerText}</span>
                            </div>
                          );
                        }}
                      >
                        <TorneoTabFixture
                          torneo={torneo}
                          highlightClubId={highlightClubId}
                        ></TorneoTabFixture>
                      </PivotItem>
                      {torneo.detalle.muestraTabla &&
                        torneo.fases.filter((f) => todosContraTodos(f)).length > 0 && (
                          <PivotItem
                            headerText="Posiciones"
                            itemKey={'2'}
                            onRenderItemLink={(itemProps) => {
                              return (
                                <div>
                                  <span>{itemProps?.headerText}</span>
                                </div>
                              );
                            }}
                          >
                            <Pivot id="posiciones" aria-label="" className="text-left">
                              {torneo.fases
                                .filter((f) => todosContraTodos(f))
                                .map((fase, iFase) => {
                                  return (
                                    <PivotItem headerText={fase.nombre} key={genkey()}>
                                      {fase.zonas
                                        .filter((zona) => zona.nombre !== PARTIDOS_EXTRA_ZONA_NAME)
                                        .map((zona) => {
                                          return (
                                            <div key={genkey()}>
                                              {fase.zonas.filter(
                                                (zona) => zona.nombre !== PARTIDOS_EXTRA_ZONA_NAME,
                                              ).length > 1 && <ZonaFixture zona={zona} />}
                                              <TorneoTabTablaPosiciones
                                                tablaPosiciones={zona.tabla}
                                                tablaGeneralPosiciones={zona.tablaGeneral}
                                                highlightClubId={highlightClubId}
                                              ></TorneoTabTablaPosiciones>
                                            </div>
                                          );
                                        })}
                                    </PivotItem>
                                  );
                                })}
                            </Pivot>
                          </PivotItem>
                        )}

                      {torneo.detalle.muestraGoleadores && (
                        <PivotItem
                          headerText="Goleadores"
                          itemKey={'3'}
                          onRenderItemLink={(itemProps) => {
                            return (
                              <div>
                                <span>{itemProps?.headerText}</span>
                              </div>
                            );
                          }}
                        >
                          <TorneoTabGoleadores
                            goleadores={torneo.goleadores}
                            highlightClubId={highlightClubId}
                          ></TorneoTabGoleadores>
                        </PivotItem>
                      )}

                      {torneo.fases.some((f) => f.zonas.some((z) => z.tablaFairplay.length > 0)) && (
                        <PivotItem
                          headerText="Fairplay"
                          itemKey={'4'}
                          onRenderItemLink={(itemProps) => {
                            return (
                              <div>
                                <span>{itemProps?.headerText}</span>
                              </div>
                            );
                          }}
                        >
                          <TorneoTabTablaGeneralPosicionesFairplay
                            highlightClubId={highlightClubId}
                            tablaFairplay={torneo.fases
                              .flatMap((fase) => fase.zonas)
                              .flatMap((zona) => zona.tablaFairplay)}
                            tablaGeneralPosiciones={torneo.fases
                              .flatMap((fase) => fase.zonas)
                              .flatMap((zona) => zona.tablaGeneral)
                              .filter((tg) => tg.tipo === 'F')}
                          ></TorneoTabTablaGeneralPosicionesFairplay>
                        </PivotItem>
                      )}
                    </Pivot>
                  </div>
                </div>
              </>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
